import React, { useState, useEffect } from "react";
import { Form, Input, Select, Button, message, Modal } from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { getDatabase, update, ref } from "firebase/database";
import { getStorage } from "firebase/storage";
import Firebase from "../../Firebase/Firebase";
import { typeFieldsMap } from "./AddLessonComponent";
import { useAuth } from "../../AuthContext/AuthContext";

const { TextArea } = Input;
const db = getDatabase(Firebase);
const storage = getStorage(Firebase);

const EditLessonComponent = ({
  lessonId,
  lessonData,
  pageIndex,
  componentData,
  componentIndex,
  onSuccess,
  onCancel
}) => {
  const { canAccessModule } = useAuth();
  const canUpdateLesson = canAccessModule("LESSONS", "update");
  const [form] = Form.useForm();
  const [uploading, setUploading] = useState(false);
  const [fileFields, setFileFields] = useState({});

  useEffect(() => {
    if (componentData) {
      // Initialize form with component data
      const initialValues = { ...componentData, type: componentData.type };

      // Handle links array if it exists
      if (componentData.links) {
        initialValues.links = componentData.links.map(link => ({ url: link.url }));
      }

      form.setFieldsValue(initialValues);

      // Track which fields are files
      const fields = {};
      (typeFieldsMap[componentData.type] || []).forEach(field => {
        if (field.isFile && componentData[field.name]) {
          fields[field.name] = componentData[field.name];
        }
      });
      setFileFields(fields);
    }
  }, [componentData, form]);

  const uploadFileAndGetURL = async (file, fieldName) => {
    if (!file) return fileFields[fieldName] || null;

    const fileName = `${Date.now()}_${file.name}`;
    const fileRef = storageRef(storage, `System/Lessons/${lessonId}/${fileName}`);
    const snapshot = await uploadBytes(fileRef, file);
    return await getDownloadURL(snapshot.ref);
  };

  const handleFinish = async (values) => {
    if (!canUpdateLesson) {
      message.error("You do not have permission to update lessons.");
      return;
    }

    try {
      setUploading(true);

      // Create updated component data
      const updatedComponent = { type: values.type };
      const typeFields = typeFieldsMap[values.type] || [];

      // Process all fields
      for (const field of typeFields) {
        if (field.isArray) continue;

        if (values[field.name] !== undefined) {
          if (field.isFile) {
            // Only upload if a new file was selected
            if (values[field.name]?.file) {
              updatedComponent[field.name] = await uploadFileAndGetURL(values[field.name], field.name);
            } else if (fileFields[field.name]) {
              // Keep existing file if no new one was selected
              updatedComponent[field.name] = fileFields[field.name];
            }
          } else {
            updatedComponent[field.name] = values[field.name];
          }
        }
      }

      // Process links array if it exists
      if (values.links) {
        updatedComponent.links = values.links.map(link => ({
          url: link.url,
        }));
      }

      // Update the lesson data structure
      const updatedPages = [...lessonData.pages];
      updatedPages[pageIndex].components[componentIndex] = updatedComponent;

      // Save to Firebase
      await update(ref(db, `talmidsdb/lessons/${lessonId}`), { pages: updatedPages });

      message.success("Component updated successfully");
      onSuccess();
    } catch (error) {
      console.error("Failed to update component:", error);
      message.error("Unable to update component: " + error.message);
    } finally {
      setUploading(false);
    }
  };

  const renderArrayField = (field) => {
    return (
      <Form.List name={field.name}>
        {(fields, { add, remove }) => (
          <div>
            {fields.map(({ key, name, ...restField }) => (
              <div key={key} style={{ display: 'flex', marginBottom: 8 }}>
                {field.fields.map((subField) => (
                  <Form.Item
                    {...restField}
                    name={[name, subField.name]}
                    label={fields.length > 1 ? '' : subField.label}
                    rules={[{ required: true, message: `${subField.label} is required` }]}
                    style={{ flex: 1, marginRight: 8 }}
                  >
                    <Input placeholder={`Enter ${subField.label.toLowerCase()}`} />
                  </Form.Item>
                ))}
                {fields.length > 0 && (
                  <Form.Item>
                    <MinusCircleOutlined
                      onClick={() => remove(name)}
                      style={{ color: 'red', marginTop: fields.length > 1 ? '0' : '32px' }}
                    />
                  </Form.Item>
                )}
              </div>
            ))}
            <Form.Item>
              <Button
                type="dashed"
                onClick={() => add()}
                block
                icon={<PlusOutlined />}
              >
                Add Link
              </Button>
            </Form.Item>
          </div>
        )}
      </Form.List>
    );
  };

  const renderField = (field) => {
    if (field.isArray) {
      return renderArrayField(field);
    }

    if (field.isFile) {
      return (
        <Form.Item
          key={field.name}
          label={field.label}
          name={field.name}
          valuePropName="file"
          getValueFromEvent={(e) => (e.target.files ? e.target.files[0] : null)}
        >
          <Input
            type="file"
            accept={field.name.includes('audio') ? 'audio/*' : 'image/*,.pdf'}
            style={{ width: "100%" }}
          />
          {fileFields[field.name] && (
            <div style={{ marginTop: 8 }}>
              <small>Current file: {fileFields[field.name].split('/').pop()}</small>
            </div>
          )}
        </Form.Item>
      );
    }

    return (
      <Form.Item
        key={field.name}
        label={field.label}
        name={field.name}
        rules={[{ required: true, message: `${field.label} is required` }]}
      >
        {field.component || <Input />}
      </Form.Item>
    );
  };

  return (
    <Modal
      title={`Edit ${componentData?.type} Component`}
      visible={true}
      onCancel={onCancel}
      footer={[
        <Button key="back" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={uploading}
          disabled={!canUpdateLesson}
          onClick={() => form.submit()}
        >
          Save Changes
        </Button>,
      ]}
      width={800}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{ type: componentData?.type }}
      >
        <Form.Item
          label="Component Type"
          name="type"
          rules={[{ required: true, message: "Component type is required" }]}
        >
          <Select disabled placeholder="Component type">
            <Select.Option value={componentData?.type}>
              {componentData?.type?.charAt(0).toUpperCase() + componentData?.type?.slice(1)}
            </Select.Option>
          </Select>
        </Form.Item>

        {componentData?.type &&
          typeFieldsMap[componentData.type]?.map((field) => renderField(field))}
      </Form>
    </Modal>
  );
};

export default React.memo(EditLessonComponent);
